<template>
  <div :class="`slds-tabs_${variant}`">
    <ul :class="`slds-tabs_${variant}__nav`" role="tablist">
      <slot v-for="(tab, index) in tabs" name="tab" :tab="tab" :index="index">
        <li
          :key="tab.name"
          :class="[
            `slds-tabs_${variant}__item`,
            {
              'slds-is-active': tab.isActive,
              'slds-is-disabled': tab.isDisabled,
              ...tab.titleClass,
            },
          ]"
          :title="tab.title"
          role="presentation"
        >
          <a
            v-uni-id="`${tab.name}__item`"
            v-uni-aria-controls="tab.name"
            role="tab"
            :class="`slds-tabs_${variant}__link slds-grid slds-grid_vertical-align-center`"
            :aria-selected="tab.isActive"
            :tabindex="tab.isActive ? '0' : '-1'"
            :href="tab.activeHref"
            :target="tab.target"
            @click="tab.target != '_blank' && navigateToTab(tab.name, $event)"
          >
            {{ tab.title }}
            <span
              v-if="tab.badge !== undefined"
              class="slds-badge slds-m-left_xx-small"
              >{{ tab.badge }}</span
            >
          </a>
        </li>
      </slot>
    </ul>

    <slot></slot>
  </div>
</template>

<script>
/* eslint-disable */
import Vue from 'vue';
import { createUniqIdsMixin } from 'vue-uniq-ids';
import invariant from 'invariant';
import TabComponent from './Tab.vue';

export default Vue.extend({
  name: 'SldsTabs',
  /**
   * Provide variant for children <tab></tab>
   */
  provide() {
    return {
      variant: this.variant,
    };
  },
  props: {
    active: { type: String },
    /**
     * Rendering style of the tab row.
     */
    variant: {
      type: String,
      default: 'default',
      validator: (val) => ['default', 'scoped'].includes(val),
    },
  },
  data() {
    return {
      tabs: [],
      tabsRef: new Map(),
      activeTab: this.active,
    };
  },
  watch: {
    active: 'updateTabs',
    activeTab: 'updateTabs',
  },
  created() {
    // create shared scope for uniq ids, inherited by children
    this.$options.uniqIdsConfig = {
      template: 'tab-%arg[0]%-%qinu%',
      scope: {},
    };
  },
  mounted() {
    this.tabs = this.$children.filter(
      (cmp) => cmp.$options.name === TabComponent.name
    );
    this.tabs.forEach((cmp) => {
      this.tabsRef.set(cmp.name, cmp);
    });
    this.tabs.forEach((cmp) => {
      cmp.$on('activate', (name, tab, evt) => {
        if (!tab.isActive) {
          this.navigateToTab(name);
        }
      });
    });
    this.updateTabs();
  },
  methods: {
    async navigateToTab(to, evt) {
      if (evt) {
        evt.preventDefault();
      }
      if (!(await this.beforeChangeTab(to))) {
        return;
      }
      this.changeTab(to, this.activeTab);
    },
    async beforeChangeTab(to, callback) {
      const toTab = this.tabsRef.get(to);
      invariant(toTab, `tab "${to}" not found`);
      if (toTab.beforeChange) {
        const allowed = await toTab.beforeChange(toTab);
        if (process.env.NODE_ENV !== 'production' && !allowed) {
          console.log(
            `slds-tabs: tab change to "${to}" not allowed by beforeChange`
          );
        }
        return allowed;
      }
      return true;
    },
    changeTab(to, from) {
      if (from === to) {
        return;
      }
      const fromTab = this.tabsRef.get(from);
      const toTab = this.tabsRef.get(to);
      if (from) {
        invariant(fromTab, `tab "${from}" not found`);
        fromTab.isActive = false;
      }
      if (to) {
        invariant(toTab, `tab "${to}" not found`);
        toTab.isActive = true;
      }
      this.activeTab = to;
      this.$emit('update:active-tab', to, from);
      this.tryChangeRoute(toTab);
    },
    tryChangeRoute(tab) {
      if (this.$router && tab.routeLocation) {
        this.$router.push(tab.routeLocation);
      }
    },
    updateTabs() {
      this.tabs.forEach((cmp) => {
        cmp.isActive = this.activeTab === cmp.name;
      });
    },
  },
  mixins: [createUniqIdsMixin({ scope: false })],
});
</script>
<style lang="scss">
@use '../../../../styles/variables';

.slds-tabs_default {
  .slds-is-disabled {
    pointer-events: none;
    .slds-tabs_default__link {
      color: variables.$color-text-button-default-disabled;
    }
    .slds-badge {
      color: variables.$color-text-button-brand-disabled;
    }
  }
}
</style>
